import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Input, Modal, Select, Space, Typography, message } from 'antd';
import {
  ExclamationCircleOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpMasterShell,
  useErpMasterQuery
} from '../../../../components/common/erp-grid';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import { buildExpenseColumns } from './expenseEntryColumns';
import { expenseEntryApi } from './expenseEntryApi';
import { EXPENSE_TYPES } from './expenseEntryConstants';
import ExpenseEntryModal from './ExpenseEntryModal';
// Stage 6B.9F — period guard for Post action.
import { accountsApi } from '../accountsApi';
import {
  classifyPeriodForDate, periodGuardError, loadPeriodsForGuard
} from '../accountingPeriodGuard';
import '../accounts.css';

const { Title } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Expense Entry page (Stage 6B.7).
//
// Single grid hosts paid + credit expenses with a Type filter in the
// toolbar. Modal handles both flows. Post + Cancel are L1 Approval-
// MFA gated and use the existing Modal.confirm `afterClose: resolve`
// pattern so MFA never stacks on the form modal.
// ─────────────────────────────────────────────────────────────────

function defaultOnError(err) {
  const msg = err?.response?.data?.error?.message ||
    err?.message || 'Failed to load expenses.';
  message.error({ content: msg, key: 'expense-list' });
}

export default function ExpenseEntryPage() {
  const [expenseTypeFilter, setExpenseTypeFilter] = useState('all');

  // Memoise the adapter on the filter value so its reference is stable
  // across renders. An inline adapter would mint a new object every
  // render → `fetchOnce` in useErpMasterQuery recomputes → its
  // auto-fetch useEffect re-fires → infinite request loop.
  const adapter = useMemo(() => ({
    list: (q) => {
      const extra = expenseTypeFilter === 'all'
        ? null
        : { expenseType: expenseTypeFilter };
      return expenseEntryApi.list({ ...(q || {}), ...(extra || {}) });
    }
  }), [expenseTypeFilter]);

  const { gridHandlers, refetch, query } = useErpMasterQuery({
    adapter,
    pageSize: 25,
    onError: defaultOnError
  });
  const currentPage = query && query.page     ? query.page     : 1;
  const pageSize    = query && query.pageSize ? query.pageSize : 25;
  const columns = useMemo(
    () => buildExpenseColumns({ currentPage, pageSize }),
    [currentPage, pageSize]);

  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add');
  const [drawerRecord, setDrawerRecord] = useState(null);

  // Stage 6B.9F — period guard fetch for Post action.
  const [periods, setPeriods] = useState([]);
  useEffect(() => {
    let cancelled = false;
    loadPeriodsForGuard(accountsApi).then((out) => {
      if (cancelled) return;
      setPeriods((out && out.rows) || []);
    });
    return () => { cancelled = true; };
  }, []);

  const openAdd = useCallback(() => {
    setDrawerMode('add'); setDrawerRecord(null); setDrawerOpen(true);
  }, []);
  const openWithFetch = useCallback(async (record, mode) => {
    try {
      const full = await expenseEntryApi.get(record.id);
      setDrawerMode(mode);
      setDrawerRecord(full);
      setDrawerOpen(true);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to load expense.');
    }
  }, []);
  const openEdit = useCallback((record) => openWithFetch(record, 'edit'), [openWithFetch]);
  const openView = useCallback((record) => openWithFetch(record, 'view'), [openWithFetch]);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const onSaved = useCallback(async () => {
    setDrawerOpen(false);
    await refetch();
  }, [refetch]);

  const handlePost = useCallback(async (record) => {
    if (record.status !== 'draft') {
      message.warning(`Only draft expenses can be posted. This expense is already ${record.status}.`);
      return;
    }
    // Stage 6B.9F — period guard (client-side pre-check).
    const cls = classifyPeriodForDate(periods, record.expenseDate);
    const guard = periodGuardError(cls);
    if (guard) {
      message.error(guard.message);
      return;
    }
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: `Post expense ${record.expenseNo}?`,
        icon: <CheckCircleOutlined style={{ color: '#3f8600' }} />,
        content: `Posting will update ledger balances and lock the row. You will be asked for a 6-digit Approval MFA code to confirm.`,
        okText: 'Post',
        cancelText: 'Cancel',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      await runWithApproval({
        operationKey:   'accounts.expense.post',
        resourceType:   'expense_entry',
        resourceId:     record.id,
        operationLabel: 'Post expense entry',
        resourceLabel:  `${record.expenseNo} · ₹${Number(record.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        action: (approvalMfaChallengeId) =>
          expenseEntryApi.post(record.id, { approvalMfaChallengeId })
      });
      message.success('Expense posted.');
      await refetch();
    } catch (err) {
      if (err && err.name === 'ApprovalMfaCancelled') return;
      message.error(err?.response?.data?.error?.message || 'Could not post the expense. Please check the form and try again.');
    }
  }, [refetch, runWithApproval, periods]);

  const handleCancel = useCallback(async (record) => {
    if (record.status !== 'posted') {
      message.warning(`Only posted expenses can be cancelled (this one is ${record.status}).`);
      return;
    }
    const reason = await new Promise((resolve) => {
      let value = '';
      let okClicked = false;
      const ReasonForm = () => (
        <div>
          <p style={{ marginTop: 0 }}>
            Cancelling will REVERSE the ledger impact of this posted expense.
            Provide a short reason (audit trail).
          </p>
          <Form layout="vertical">
            <Form.Item label="Reason" required>
              <Input.TextArea rows={3} maxLength={500}
                onChange={(e) => { value = e.target.value; }} />
            </Form.Item>
          </Form>
        </div>
      );
      Modal.confirm({
        title: `Cancel expense ${record.expenseNo}?`,
        icon: <ExclamationCircleOutlined />,
        content: <ReasonForm />,
        okText: 'Continue',
        okButtonProps: { danger: true },
        cancelText: 'Keep',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked ? value : null)
      });
    });
    if (!reason) return;
    if (String(reason).trim().length < 3) {
      message.error('Reason must be at least 3 characters.');
      return;
    }
    try {
      await runWithApproval({
        operationKey:   'accounts.expense.cancel',
        resourceType:   'expense_entry',
        resourceId:     record.id,
        operationLabel: 'Cancel expense entry',
        resourceLabel:  `${record.expenseNo}`,
        action: (approvalMfaChallengeId) =>
          expenseEntryApi.cancel(record.id, { reason: String(reason).trim() }, { approvalMfaChallengeId })
      });
      message.success('Expense cancelled.');
      await refetch();
    } catch (err) {
      if (err && err.name === 'ApprovalMfaCancelled') return;
      message.error(err?.response?.data?.error?.message || 'Failed to cancel expense.');
    }
  }, [refetch, runWithApproval]);

  const handleDelete = useCallback(async (record) => {
    if (record.status !== 'draft') {
      message.warning(`Only draft expenses can be deleted. This expense is already ${record.status} — use Cancel instead.`);
      return;
    }
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: `Delete draft ${record.expenseNo}?`,
        icon:  <ExclamationCircleOutlined />,
        content: 'The draft will be soft-deleted. You will be asked for a 6-digit Approval MFA code to confirm.',
        okText: 'Delete', okButtonProps: { danger: true }, cancelText: 'Keep',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      // Stage 6B.9F — Approval-MFA L1 for draft delete.
      await runWithApproval({
        operationKey:   'accounts.expense.delete',
        resourceType:   'expense_entry',
        resourceId:     record.id,
        operationLabel: 'Delete draft expense entry',
        resourceLabel:  `${record.expenseNo}`,
        action: (approvalMfaChallengeId) =>
          expenseEntryApi.remove(record.id, { approvalMfaChallengeId })
      });
      message.success('Draft deleted.');
      await refetch();
    } catch (e) {
      if (e && e.name === 'ApprovalMfaCancelled') return;
      message.error(e?.response?.data?.error?.message || 'Could not delete the draft. Please refresh and try again.');
    }
  }, [refetch, runWithApproval]);

  const actionsProp     = useMemo(() => ({ add: true, view: true, edit: true, delete: false }), []);
  const pageSizeOptions = useMemo(() => ['15', '25', '50', '100'], []);

  const contextMenuActions = useMemo(() => ([
    { key: 'view',   label: 'View',         icon: <EyeOutlined />,            onClick: openView },
    { key: 'edit',   label: 'Edit Draft',   icon: <EditOutlined />,           onClick: openEdit },
    { key: 'post',   label: 'Post',         icon: <CheckCircleOutlined />,    onClick: handlePost },
    { key: 'cancel', label: 'Cancel',       icon: <CloseCircleOutlined />,    danger: true, onClick: handleCancel },
    { key: 'delete', label: 'Delete Draft', icon: <DeleteOutlined />,         danger: true, onClick: handleDelete }
  ]), [openView, openEdit, handlePost, handleCancel, handleDelete]);

  const toolbarExtra = useMemo(() => (
    <Space size="small">
      <span style={{ fontSize: 12, color: '#666' }}>Type:</span>
      <Select
        size="small" style={{ width: 180 }}
        value={expenseTypeFilter}
        onChange={(v) => setExpenseTypeFilter(v)}
        options={[
          { value: 'all', label: 'All paid + credit' },
          ...EXPENSE_TYPES
        ]}
      />
    </Space>
  ), [expenseTypeFilter]);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
      <ErpDataGrid
        title="Expense Entries"
        gridId="accounts-expense-entries-v1"
        columns={columns}
        rowKey="id"
        rowHeight="compact"
        stickyHeader
        selectable
        actionWidth={56}
        pageSizeOptions={pageSizeOptions}
        searchable
        searchPlaceholder="Search by expense no / reference / remarks…"
        searchShortcut
        exportable
        exportFilename="expense-entries"
        actions={actionsProp}
        addButtonText="Add Expense"
        emptyTitle="No expenses yet"
        emptyText="Click Add Expense to record your first expense entry."
        emptyCtaLabel="Add Expense"
        onAdd={openAdd}
        onEdit={openEdit}
        onView={openView}
        onRowClick={openView}
        contextMenuActions={contextMenuActions}
        toolbarExtra={toolbarExtra}
        {...gridHandlers}
      />
      <ExpenseEntryModal
        open={drawerOpen}
        mode={drawerMode}
        record={drawerRecord}
        onClose={closeDrawer}
        onSaved={onSaved}
      />
      {ApprovalMfaModalSlot}
      </div>
    </ErpMasterShell>
  );
}
